import { Primitive } from '@radix-ui/react-primitive';
import { type ComponentProps, memo, useMemo } from 'react';

import { type Clip } from '@air/api/types';
import {
  getClipSpritesheetAspectRatio,
  getClipSpritesheetColumns,
  getClipSpritesheetFrameCount,
  getClipSpritesheetRows,
  getSpritesheetBackgroundPosition,
} from '@air/utils-clips';

import {
  PLAYER_PREVIEW_SPRITESHEET_NAME,
  type PlayerPreviewScopeProps,
  usePlayerPreviewContext,
} from './PlayerPreviewContextProvider';

const PLAYER_PREVIEW_SPRITESHEET_MAX_HEIGHT = 120;

export type PlayerPreviewSpritesheetProps = ComponentProps<typeof Primitive.div> & {
  clip: Clip;
};

export const PlayerPreviewSpritesheet = memo(
  ({ __scopePlayerPreview, clip, style, ...restOfProps }: PlayerPreviewScopeProps<PlayerPreviewSpritesheetProps>) => {
    const context = usePlayerPreviewContext(PLAYER_PREVIEW_SPRITESHEET_NAME, __scopePlayerPreview);

    const scaledAspectRatio = useMemo(() => {
      const scaledWidth = Math.round(
        PLAYER_PREVIEW_SPRITESHEET_MAX_HEIGHT * getClipSpritesheetAspectRatio(clip.assets.spriteSheet),
      );

      return {
        height: PLAYER_PREVIEW_SPRITESHEET_MAX_HEIGHT,
        width: scaledWidth,
      };
    }, [clip.assets.spriteSheet]);

    const backgroundSize = useMemo(() => {
      const columns = getClipSpritesheetColumns(clip.assets.spriteSheet);
      const rows = getClipSpritesheetRows(clip.assets.spriteSheet);
      const scaledWidth = scaledAspectRatio.width * columns;
      const scaledHeight = scaledAspectRatio.height * rows;

      return `${scaledWidth}px ${scaledHeight}px`;
    }, [scaledAspectRatio.height, scaledAspectRatio.width, clip.assets.spriteSheet]);

    const backgroundPosition = useMemo(() => {
      return getSpritesheetBackgroundPosition({
        columns: getClipSpritesheetColumns(clip.assets.spriteSheet),
        frameCount: getClipSpritesheetFrameCount(clip.assets.spriteSheet),
        frameHeight: scaledAspectRatio.height,
        frameWidth: scaledAspectRatio.width,
        position: context.position,
      });
    }, [scaledAspectRatio.height, scaledAspectRatio.width, clip.assets.spriteSheet, context.position]);

    return (
      <Primitive.div
        data-testid="PLAYER_PREVIEW_SPRITESHEET"
        {...restOfProps}
        style={{
          backgroundImage: `url(${clip.assets.spriteSheet?.src})`,
          backgroundPosition,
          backgroundSize,
          ...scaledAspectRatio,
          ...style,
        }}
      />
    );
  },
);

PlayerPreviewSpritesheet.displayName = 'PlayerPreviewSpritesheet';
